// What a Copilot answer is made of. Pure, no database.
//
// Read docs/apd-copilot/FEATURE_CHARTER.md before changing anything here.
//
// An answer is prose plus two structured parts the UI renders separately:
//
//   citations  the records the answer rests on. The model can only cite what a
//              tool actually returned in this turn: it writes [C3], the server
//              maps C3 to a record it handed out, and an unknown marker is
//              dropped. A citation the server never issued cannot appear.
//   proposals  things a person may choose to do: save a handoff, review email
//              drafts, record a relationship candidate, run a PartsBase search.
//              A proposal writes NOTHING. Each one is a card; the write happens
//              only when an authenticated person presses its button, through
//              the same server checks as any other request.

import { z } from 'zod';
import { HandoffSummary, ATTENTION_STATES } from './attention';
import { DRAFT_KINDS } from './drafts';
import { RELATIONSHIP_KINDS, EVIDENCE_GRADES } from './candidates';

/** Where a fact came from. The UI never collapses these into one voice. */
export const PROVENANCE = ['database', 'email', 'public_research', 'inference', 'confirmed'] as const;
export type Provenance = (typeof PROVENANCE)[number];
export const PROVENANCE_LABEL: Record<Provenance, string> = {
  database: 'APD record',
  email: 'Email',
  public_research: 'Public source',
  inference: 'Copilot inference',
  confirmed: 'Broker-confirmed',
};

export const CITATION_KINDS = [
  'rfq', 'rfq_line', 'email', 'gmail', 'vendor_quote', 'vendor_reply', 'client_quote',
  'part_intelligence', 'flis', 'partsbase', 'handoff', 'candidate', 'web', 'supplier_contact',
] as const;
export type CitationKind = (typeof CITATION_KINDS)[number];

export interface Citation {
  /** Marker the model used, e.g. "C3". Stable within one answer. */
  id: string;
  kind: CitationKind;
  provenance: Provenance;
  label: string;
  /** Public id or key of the record, when it has one. Never a UUID. */
  ref?: string | null;
  /** In-app path or external URL. External URLs are only ever http(s). */
  href?: string | null;
  /** Short excerpt shown on expand. Never a whole email. */
  excerpt?: string | null;
  /** A-D, public sources only (see candidates.ts). */
  grade?: (typeof EVIDENCE_GRADES)[number] | null;
  at?: string | null;
}

// ---------------------------------------------------------------------------
// Proposals: cards, never writes
// ---------------------------------------------------------------------------

/** A supplier recipient Copilot found on a public web page in this answer,
 *  not in APD records (charter ADR-1). Built by the server from the search
 *  results it saw; a person must confirm it before the draft can be saved. */
export const WebRecipient = z.object({
  email: z.string().email().max(254),
  url: z.string().url().max(2000),
  title: z.string().max(300).nullable(),
  /** The address appears in text the model quoted from that page. */
  seenInSource: z.boolean(),
  /** The address's domain is the page's host or a parent of it. */
  domainMatchesSource: z.boolean(),
});
export type WebRecipient = z.infer<typeof WebRecipient>;

export const DraftProposal = z.object({
  kind: z.enum(DRAFT_KINDS),
  /** A recipient email address. Supplier drafts: exactly one recipient. */
  to: z.array(z.string().email().max(254)).min(1).max(5),
  cc: z.array(z.string().email().max(254)).max(5).optional(),
  subject: z.string().trim().min(1).max(300),
  body: z.string().trim().min(1).max(8000),
  /** Display name for the recipient (a supplier or customer company). */
  recipientLabel: z.string().max(200).optional(),
  /** Why this recipient: shown on the card. */
  reason: z.string().max(600).optional(),
  /** Gmail thread to reply in, when replying. Verified against the mailbox. */
  replyToGmailThreadId: z.string().regex(/^[0-9a-f]{8,32}$/i).optional(),
  /** Recipients found on the public web. Set by the server only. */
  webRecipients: z.array(WebRecipient).max(10).optional(),
});
export type DraftProposal = z.infer<typeof DraftProposal>;

export const Proposal = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('handoff'),
    attention: z.enum(ATTENTION_STATES),
    assigneeMembershipId: z.string().uuid().nullable().optional(),
    summary: HandoffSummary,
    /** The words the user asked to pass on, kept verbatim for the human note. */
    noteSuggestion: z.string().max(2000).optional(),
  }),
  z.object({
    type: z.literal('email_drafts'),
    /** Mailbox the drafts would come from; re-checked when a person saves. */
    mailboxId: z.string().regex(/^mbx_[0-9A-Za-z]{10,40}$/).nullable(),
    drafts: z.array(DraftProposal).min(1).max(15),
  }),
  z.object({
    type: z.literal('relationship_candidate'),
    lineItemPublicId: z.string().regex(/^li_[0-9A-Za-z]{10,40}$/).nullable(),
    subjectIdentifier: z.string().trim().min(1).max(100),
    relationship: z.enum(RELATIONSHIP_KINDS),
    proposedValue: z.string().trim().min(1).max(200),
    rationale: z.string().trim().min(1).max(1500),
    grade: z.enum(EVIDENCE_GRADES),
    /** Citation markers from this answer that support it. */
    evidence: z.array(z.string().regex(/^C\d{1,3}$/)).min(1).max(10),
  }),
  z.object({
    type: z.literal('partsbase_search'),
    lineItemPublicId: z.string().regex(/^li_[0-9A-Za-z]{10,40}$/),
    partNumber: z.string().max(100),
    reason: z.string().max(400),
  }),
]);
export type Proposal = z.infer<typeof Proposal>;

// ---------------------------------------------------------------------------
// Streaming events (server -> client, Server-Sent Events)
// ---------------------------------------------------------------------------

export type StreamEvent =
  | { type: 'thread'; threadId: string; userMessageId: string }
  | { type: 'status'; label: string }
  | { type: 'text'; delta: string }
  | { type: 'done'; message: CopilotMessageView }
  | { type: 'error'; message: string; retryable: boolean };

export interface ToolTraceEntry {
  tool: string;
  ok: boolean;
  ms: number;
  /** One line for the details view. Never email bodies or secrets. */
  summary: string;
}

export interface CopilotMessageView {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  author: string | null;
  createdAt: string;
  citations: Citation[];
  proposals: Proposal[];
  trace: ToolTraceEntry[];
  status: 'complete' | 'partial' | 'error';
  /** Why an answer stopped early, in words for the user. */
  limitNote: string | null;
  model: string | null;
  feedback: 'up' | 'down' | null;
}

/** Map [C3]-style markers in model text to the citations this turn issued.
 *  Unknown markers are removed; known ones are kept in first-use order. */
export function resolveCitationMarkers(text: string, issued: Map<string, Citation>): { text: string; citations: Citation[] } {
  const used: Citation[] = [];
  const seen = new Set<string>();
  const out = text.replace(/\[(C\d{1,3})(?:\s*,\s*(C\d{1,3}))*\]/g, (m) => {
    const ids = m.slice(1, -1).split(/\s*,\s*/);
    const kept = ids.filter((id) => issued.has(id));
    for (const id of kept) {
      if (!seen.has(id)) { seen.add(id); used.push(issued.get(id)!); }
    }
    return kept.length ? `[${kept.join(', ')}]` : '';
  });
  return { text: out.replace(/[ \t]+\n/g, '\n'), citations: used };
}

/** Only http(s) links leave the app. */
export function safeExternalHref(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}
